import React from 'react';
import { Container, TextField, Button, Typography, Grid, Paper, Box, Avatar, styled, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Helmet } from 'react-helmet-async';

const styles = {
  textCenter: {
    textAlign: 'center',
    color: '#666',
  },
  smallText: {
    fontSize: '0.8rem',
  },
};

const RedAsterisk = styled('span')({
  color: 'red',
});

const adminLoginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  password: Yup.string()
    .required('Password is required'),
});

const AdminLogin = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: adminLoginSchema,
    onSubmit: async (values) => {
      try {
        await login(values.email, values.password);
      } catch (error) {
        console.error('Error logging in:', error);
        formik.setErrors({ general: 'Invalid email or password' });
      }
    },
  });

  const handleForgetPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <Box
      style={{
        height: '100vh',
        background: 'linear-gradient(-20deg, #616161 0%, #9bc5c3 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        position: 'fixed',
        width: '100%',
        top: 0,
        left: 0,
        overflow: 'hidden',
      }}
    >
      <Helmet>
        <title>Admin SignIn</title>
      </Helmet>
      <Container component={Paper} elevation={6} maxWidth="xs" style={{
        padding: 30,
        borderRadius: '15px',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
        position: 'relative',
        zIndex: 1,
      }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container justifyContent="center" alignItems="center">
            <Grid item>
              <Avatar src='/cirapng.png' alt="Logo" style={{
                width: 80,
                height: 100,
                marginBottom: 20,
                overflow: 'visible',
              }} />
            </Grid>
          </Grid>
          <Typography variant="h5" component="h2" gutterBottom style={{ textAlign: 'center', fontWeight: 'bold', color: '#333', marginBottom: 10 }}>
            Admin Login
          </Typography>
          <Typography variant="subtitle1" component="h2" gutterBottom style={{ ...styles.textCenter, ...styles.smallText, marginBottom: 15 }}>
            Access your admin dashboard
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={
                  <>
                    {'email'} <RedAsterisk>*</RedAsterisk>
                  </>
                }
                name="email"
                variant="outlined"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={
                  <>
                    {'password'} <RedAsterisk>*</RedAsterisk>
                  </>
                }
                name="password"
                type="password"
                variant="outlined"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
              />
            </Grid>
          </Grid>
          {formik.errors.general && (
            <Typography color="error" style={{ marginTop: 15, textAlign: 'center' }}>
              {formik.errors.general}
            </Typography>
          )}
          <Grid container justifyContent="flex-end" style={{ marginTop: 15, marginBottom: 15 }}>
            <Button onClick={handleForgetPassword} style={{ textDecoration: 'underline', cursor: 'pointer' }}>
              Forgot Password?
            </Button>
          </Grid>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color={formik.isSubmitting ? 'secondary' : 'primary'}
            style={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '12px',
            }}
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting && (
              <CircularProgress
                size={24}
                style={{
                  position: 'absolute',
                  color: 'white',
                }}
              />
            )}
            <span style={{ visibility: formik.isSubmitting ? 'hidden' : 'visible' }}>Sign In</span>
          </Button>
          <Grid item xs={12} style={{ textAlign: 'center', marginTop: 20 }}>
            <Typography
              variant="subtitle1"
              component="h2"
              gutterBottom
              style={{ ...styles.textCenter }}
            >
              Don't have an account?{' '}
              <Button color="primary" onClick={() => navigate('/adminSignup')} style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                Sign Up
              </Button>
            </Typography>
          </Grid>
        </form>
      </Container>
    </Box>
  );
};

export default AdminLogin;
